import { useEffect, useState, type ReactNode } from 'react'
import { useDashboardSummary, useFacility, useFacilityConfig, useUpdateFacilityConfig } from '../index.js'
import { FacilityAdministratorContacts } from '../components/RosterHighlights.js'
import { isNotFoundError, searchParam, Skeleton } from '../../core/index.js'
import { FacilityNotConfigured } from '../components/FacilityNotConfigured.js'
import type { Facility, FacilityConfig, ScoringRules } from '../index.js'
import { navigate } from '../../core/index.js'
import { Switch } from '../../core/index.js'
import { NumberField } from '../../core/index.js'
import { BehaviorsManager, MASTER_SCOPE, type BehaviorsScope } from '../../behaviors/index.js'
import { sessionBehaviorsApi } from '../../behaviors/index.js'
import { FacilityDetailsCard } from '../components/FacilityDetailsCard.js'
import { FacilitySchedulingCard, SchedulingGroup } from '../components/FacilitySchedulingCard.js'
import { FacilityAutomationCard } from '../components/FacilityAutomationCard.js'
import { FacilityStaffingRules } from '../components/FacilityStaffingRules.js'
import { schedulingRulesOf } from '../defaultSchedulingRules.js'
import { deliveryRulesOf } from '../defaultDeliveryRules.js'
import { FacilityDeliveryCard } from '../../messages/index.js'
import { FacilityAddOnsCard } from '../../add-ons/index.js'
import { useAvailableShifts } from '../../schedule/index.js'
import { FacilityAdministratorsSection, useMe } from '../../accounts/index.js'
import { FacilityAdministratorIcon } from '../../core/index.js'
import { ConfigTabsBar } from '../components/ConfigTabsBar.js'
import { Button } from '../../core/index.js'
import { Save } from 'lucide-react'
import { LoadFailure } from '../../core/index.js'
import { HelpIcon } from '../../core/index.js'

/** The API's defaults, for a configuration loaded from an API deployed before scoring rules existed. */
const DEFAULT_SCORING_RULES: ScoringRules = { minShifts: 5, graceMinutes: 5, shortNoticeHours: 4, shortNoticeMultiplier: 1, newHireProbationDays: 90, provisionalCanMoveDown: false, ptoDeniedWorkedPoints: 2, lateFillPoints: 2, lateBridgeWithdrawalPoints: 1 }
/** Likewise for the Clinical Bridge arrival window, an hour until a facility sets one. */
const DEFAULT_ARRIVAL_WINDOW_MINUTES = 60

function ConfigCard({ title, help, children }: { title: string; /** A help topic for the info icon beside the title. */ help?: string; children: ReactNode }) {
  return (
    <div className="bg-white border border-marine/15 rounded p-5">
      <div className="text-sm font-black text-ink mb-2">
        {title}
        {help && <HelpIcon topic={help} className="ml-1" />}
      </div>
      {children}
    </div>
  )
}

/** `hint` is a line under the label saying what the setting means and what, if anything, it changes. */
function Row({ label, hint, children }: { label: string; hint?: string; children: ReactNode }) {
  return (
    <div className="flex justify-between items-center py-2.5 border-b border-marine/10 last:border-b-0 gap-4">
      <div className="min-w-0">
        <div className="text-xs text-ink-mid">{label}</div>
        {hint && <p className="mt-0.5 max-w-md text-[10.5px] leading-snug text-ink-mid/80">{hint}</p>}
      </div>
      {children}
    </div>
  )
}

const TEXT_INPUT_CLASS =
  'w-44 max-w-[55%] rounded border border-marine/30 bg-white px-2.5 py-1.5 text-right text-sm font-bold text-ink focus:outline-none focus:border-marine focus:ring-2 focus:ring-marine/20'

function NumberRow({ label, value, onChange, suffix }: { label: string; value: number; onChange: (value: number) => void; suffix?: string }) {
  return (
    <Row label={label}>
      <NumberField value={value} onChange={onChange} label={label} suffix={suffix} />
    </Row>
  )
}

function NullableNumberRow({
  label,
  hint,
  value,
  onChange,
  suffix,
}: {
  label: string
  hint?: string
  value: number | null
  onChange: (value: number | null) => void
  suffix?: string
}) {
  return (
    <Row label={label} hint={hint}>
      <span className="inline-flex items-center rounded border border-marine/30 bg-white focus-within:border-marine focus-within:ring-2 focus-within:ring-marine/20">
        <input
          type="number"
          aria-label={label}
          placeholder="Not set"
          className="w-20 bg-transparent px-2 py-1.5 text-right text-sm font-bold text-ink placeholder:font-normal placeholder:text-ink-mid/60 focus:outline-none"
          value={value ?? ''}
          onChange={(event) => onChange(event.target.value === '' ? null : Number(event.target.value))}
        />
        {suffix && <span className="pr-2 text-xs text-ink-mid select-none">{suffix}</span>}
      </span>
    </Row>
  )
}

function TextRow({ label, hint, value, onChange, placeholder }: { label: string; hint?: string; value: string; onChange: (value: string) => void; placeholder?: string }) {
  return (
    <Row label={label} hint={hint}>
      <input type="text" aria-label={label} className={TEXT_INPUT_CLASS} value={value} placeholder={placeholder} onChange={(event) => onChange(event.target.value)} />
    </Row>
  )
}

function NullableTextRow({ label, hint, value, onChange }: { label: string; hint?: string; value: string | null; onChange: (value: string | null) => void }) {
  return <TextRow label={label} hint={hint} value={value ?? ''} placeholder="Not set" onChange={(next) => onChange(next.length > 0 ? next : null)} />
}

function ReadOnlyRow({ label, hint, value }: { label: string; hint?: string; value: string }) {
  return (
    <Row label={label} hint={hint}>
      <div className="text-sm font-bold text-ink text-right">{value}</div>
    </Row>
  )
}

function ToggleRow({ label, checked, onChange }: { label: string; checked: boolean; onChange: (checked: boolean) => void }) {
  return (
    <Row label={label}>
      <Switch checked={checked} onChange={onChange} label={label} />
    </Row>
  )
}

interface ConfigurationProps {
  /** The facility whose page this is, or null when opened from the facility list with none chosen - which shows the master. */
  facilityId: string | null
  /** True when the client has several facilities, each with its own URL to page between. */
  hasFacilityRoutes: boolean
  /** True for an operator session: the facility's administrators are the client's to invite and remove. Everything else about a facility - its record, every settings tab, the add-on switches - either role edits (the user's rule of 24 October 2026: a facility administrator manages every aspect of their facilities). */
  canManageAdministrators: boolean
  /** Where the bar's back link goes: the Configuration takes the place of the shell's bar while it is open. */
  back: { href: string; label: string }
}

/**
 * The settings cards in the order the strip shows them. `staffing` (units,
 * credentials and minimum staffing) is not a tab of its own: it sits on the
 * Details tab, under the facility's record, though it is saved with the rest
 * of the settings.
 */
type SettingsSection = 'scoring' | 'thresholds' | 'bidWindows' | 'scheduling' | 'automation' | 'bridge' | 'notifications' | 'prioritization' | 'staffing'
type ConfigTab = 'details' | 'behaviors' | Exclude<SettingsSection, 'staffing'> | 'addons'

const SETTINGS_SECTIONS: readonly SettingsSection[] = ['staffing', 'scoring', 'thresholds', 'bidWindows', 'scheduling', 'automation', 'bridge', 'notifications', 'prioritization']

/** The parts of the scheduling block the Details tab edits rather than the Scheduling tab. */
const STAFFING_KEYS = ['units', 'credentials', 'minimumStaffing', 'substitutions'] as const

const TAB_LABELS: Record<ConfigTab, string> = {
  details: 'Details',
  behaviors: 'Behaviors',
  scoring: 'Scoring Rules',
  thresholds: 'Tier Thresholds',
  bidWindows: 'Bid Windows',
  scheduling: 'Scheduling',
  automation: 'Automation',
  bridge: 'Clinical Bridge',
  notifications: 'Notifications',
  prioritization: 'Partner Prioritization',
  addons: 'Add-ons',
}

/** The tab a link asked for (`/config?tab=scheduling`, the Month card's buttons); a facility's tabs only, since the master shows behaviors alone. */
function requestedConfigTab(facilityId: string | null): ConfigTab | null {
  const asked = searchParam('tab')
  return facilityId !== null && asked !== null && asked in TAB_LABELS ? (asked as ConfigTab) : null
}

/** The tab each settings section is edited on, for naming an unsaved change. */
const SECTION_LABELS: Record<SettingsSection, string> = { ...TAB_LABELS, staffing: TAB_LABELS.details }

function isSettingsSection(tab: ConfigTab): tab is Exclude<SettingsSection, 'staffing'> {
  return SETTINGS_SECTIONS.includes(tab as SettingsSection)
}

/** The settings section a tab shows: the Details tab shows the staffing rules under the facility's record. */
function sectionOf(tab: ConfigTab): SettingsSection | null {
  return tab === 'details' ? 'staffing' : isSettingsSection(tab) ? tab : null
}

/** The parts of the scheduling block the Automation tab edits: the release and the census response. */
const AUTOMATION_KEYS = ['release', 'census', 'alerts'] as const

function pick(rules: ReturnType<typeof schedulingRulesOf>, keys: readonly string[], keep: boolean): Record<string, unknown> {
  return Object.fromEntries(Object.entries(rules).filter(([key]) => keys.includes(key) === keep))
}

/** The part of the configuration each settings section edits, so an unsaved change can be named by its tab. */
const SECTION_VALUES: Record<SettingsSection, (config: FacilityConfig) => unknown> = {
  staffing: (config) => pick(schedulingRulesOf(config), STAFFING_KEYS, true),
  scoring: (config) => config.scoring,
  thresholds: (config) => config.tierThresholds,
  bidWindows: (config) => config.bidWindowTiming,
  scheduling: (config) => pick(schedulingRulesOf(config), [...STAFFING_KEYS, ...AUTOMATION_KEYS], false),
  automation: (config) => pick(schedulingRulesOf(config), AUTOMATION_KEYS, true),
  bridge: (config) => config.clinicalBridge,
  notifications: (config) => config.delivery,
  prioritization: (config) => config.prioritizationEnabled,
}

/** What the settings report upwards: whether anything is unsaved, and whether the Details tab's part or the settings tabs' part is. */
interface SettingsUnsaved {
  any: boolean
  staffing: boolean
  settings: boolean
}
const NOTHING_UNSAVED: SettingsUnsaved = { any: false, staffing: false, settings: false }

/** JSON with keys in a fixed order, so two configurations compare by what they say, not by the order a store returned their fields in. */
function stableStringify(value: unknown): string {
  if (Array.isArray(value)) {
    return `[${value.map(stableStringify).join(',')}]`
  }
  if (value !== null && typeof value === 'object') {
    const entries = Object.entries(value as Record<string, unknown>)
      .filter(([, item]) => item !== undefined)
      .sort(([a], [b]) => a.localeCompare(b))
    return `{${entries.map(([key, item]) => `${JSON.stringify(key)}:${stableStringify(item)}`).join(',')}}`
  }
  return JSON.stringify(value)
}

/** The settings tabs whose values differ between the draft and what is saved. */
function changedSections(draft: FacilityConfig, saved: FacilityConfig): SettingsSection[] {
  return SETTINGS_SECTIONS.filter((section) => stableStringify(SECTION_VALUES[section](draft)) !== stableStringify(SECTION_VALUES[section](saved)))
}

/** "Clinical Bridge", "Tier Thresholds and Clinical Bridge", "Scoring Rules, Tier Thresholds and Clinical Bridge". */
function listLabels(sections: readonly SettingsSection[]): string {
  const labels = [...new Set(sections.map((section) => SECTION_LABELS[section]))]
  return labels.length <= 1 ? labels.join('') : `${labels.slice(0, -1).join(', ')} and ${labels[labels.length - 1]}`
}

/** The facility's administrators for a facility administrator, who cannot manage them: names, phones and emails to reach a colleague (from "This facility at a glance", retired 25 October 2026). */
function FacilityAdministratorsReadOnly({ facilityId }: { facilityId: string }) {
  const administrators = useDashboardSummary(facilityId).data?.administrators
  if (!administrators) {
    return null
  }
  return (
    <div className="mt-4">
      <FacilityAdministratorContacts administrators={administrators} />
    </div>
  )
}

/** The facility's administrators, for the operator: who they are, and invite, edit or remove them. Removing one is kept in the configuration history. */
function FacilityAdministratorsCard({ facility }: { facility: Facility }) {
  const meQuery = useMe()
  return (
    <div className="mt-4 bg-white border border-marine/15 rounded p-5">
      <div className="flex items-center gap-2 text-sm font-black text-ink mb-3">
        <FacilityAdministratorIcon className="w-4 h-4 text-marine" />
        Facility Administrators
      </div>
      <p className="text-[11px] text-ink-mid mb-2">Choose an administrator's name to edit their details, send a reset link, or remove them from this facility.</p>
      <FacilityAdministratorsSection key={facility.facilityId} clientId={meQuery.data?.clientId} facility={facility} />
    </div>
  )
}

/**
 * A facility's configuration, as tabs: the facility's own record first
 * (Details, shown by default, with the units, credentials and minimum
 * staffing under it), then Behaviors, then the per-facility
 * settings cards (Scheduling among them: the rules the draft engine applies;
 * Notifications: which messages the facility texts, and when). Behaviors can be paged across facilities (and edited as a
 * master) without leaving the view; every other tab follows whichever
 * facility is showing, and steps aside while the master is. The settings
 * tabs share one draft and one Save button, so edits survive a tab change
 * and are marked on the strip until saved.
 */
export function Configuration({ facilityId, hasFacilityRoutes, canManageAdministrators, back }: ConfigurationProps) {
  const [isMaster, setIsMaster] = useState(facilityId === null)
  // Opened with no facility chosen there is nothing but the master to show, so Behaviors leads then.
  const [activeTab, setActiveTab] = useState<ConfigTab>(() => requestedConfigTab(facilityId) ?? (facilityId === null ? 'behaviors' : 'details'))
  const [unsavedSettings, setUnsavedSettings] = useState<SettingsUnsaved>(NOTHING_UNSAVED)
  const hasUnsavedSettings = unsavedSettings.any
  const [hasUnsavedDetails, setHasUnsavedDetails] = useState(false)
  const scope: BehaviorsScope = isMaster || facilityId === null ? MASTER_SCOPE : { kind: 'facility', facilityId }
  // The one facility showing, read by id: the configuration never needs the client's whole list.
  const { data: facility } = useFacility(scope.kind === 'facility' ? scope.facilityId : null)

  function changeScope(next: BehaviorsScope): void {
    setIsMaster(next.kind === 'master')
    if (next.kind === 'facility' && next.facilityId !== facilityId && hasFacilityRoutes) {
      navigate(`/facilities/${encodeURIComponent(next.facilityId)}/config`)
    }
  }

  const tabs = (Object.keys(TAB_LABELS) as ConfigTab[]).map((id) => ({
    id,
    label: TAB_LABELS[id],
    unsaved: id === 'details' ? hasUnsavedDetails || unsavedSettings.staffing : isSettingsSection(id) ? unsavedSettings.settings : false,
  }))

  return (
    <>
    <ConfigTabsBar tabs={tabs} active={activeTab} onChange={setActiveTab} back={back} />
    <div className="max-w-5xl mx-auto p-6 flex flex-col gap-5">

      {/* Always mounted: it holds the facility pager and the master, and its own unsaved state. */}
      <div hidden={activeTab !== 'behaviors'}>
        <BehaviorsManager api={sessionBehaviorsApi} scope={scope} onScopeChange={changeScope} hasOtherUnsavedChanges={hasUnsavedSettings || hasUnsavedDetails} />
      </div>

      {activeTab !== 'behaviors' && scope.kind === 'master' && (
        <div className="bg-marine-lt/40 border border-marine/15 rounded p-4 text-xs text-ink-mid">
          {TAB_LABELS[activeTab]} is set per facility. Choose a facility on the Behaviors tab to see it.
        </div>
      )}

      {scope.kind === 'facility' && (
        <>
          {/* The facility's own record, independent of whether it has a configuration yet. Keyed like the settings below. */}
          {facility && (
            <div hidden={activeTab !== 'details'}>
              <FacilityDetailsCard key={`details-${facility.facilityId}`} facility={facility} canEdit onUnsavedChange={setHasUnsavedDetails} />
              {/* The operator invites, edits and removes the facility's administrators here too, so a client with one facility (which never sees the facility list) can. */}
              {canManageAdministrators ? <FacilityAdministratorsCard facility={facility} /> : <FacilityAdministratorsReadOnly facilityId={facility.facilityId} />}
            </div>
          )}
          {/* Keyed so each facility starts from its own saved configuration rather than the previous facility's draft.
              Stays mounted across tabs so one draft, and its unsaved edits, carries through them. */}
          <div hidden={sectionOf(activeTab) === null}>
            <FacilitySettings key={scope.facilityId} facilityId={scope.facilityId} section={sectionOf(activeTab) ?? 'scoring'} onUnsavedChange={setUnsavedSettings} onOpenDetails={() => setActiveTab('details')} />
          </div>
          {/* The client's add-ons and this facility's switch for each; an operator changes it, a facility administrator sees it. */}
          {activeTab === 'addons' && <FacilityAddOnsCard key={`addons-${scope.facilityId}`} facilityId={scope.facilityId} canToggle />}
        </>
      )}
    </div>
    </>
  )
}

interface FacilitySettingsProps {
  facilityId: string
  /** The one card showing; the draft behind it is shared by every section. */
  section: SettingsSection
  onUnsavedChange: (unsaved: SettingsUnsaved) => void
  /** True for an operator session. A facility administrator saves their own facilities' settings too; this marks only the cards that are the operator's alone. */
  /** Opens the Details tab, where the units are listed. */
  onOpenDetails: () => void
}

function FacilitySettings({ facilityId, section, onUnsavedChange, onOpenDetails }: FacilitySettingsProps) {
  const configQuery = useFacilityConfig(facilityId)
  // The units the published shifts name, offered as the facility's list while it keeps none.
  const shiftsQuery = useAvailableShifts(facilityId)
  const shiftUnitIds = (shiftsQuery.data ?? []).map((shift) => shift.unitId)
  const updateMutation = useUpdateFacilityConfig(facilityId)
  const [draft, setDraft] = useState<FacilityConfig | null>(null)

  useEffect(() => {
    if (configQuery.data && draft === null) {
      setDraft(configQuery.data)
    }
  }, [configQuery.data, draft])

  // Reported upwards so the facility pager can refuse to move while these
  // edits are unsaved - moving unmounts this component and would drop them.
  // Compared field by field in a fixed order: the API's answer after a save
  // comes back from the store with its fields in whatever order it keeps
  // them, which must not read as a change.
  const saved = configQuery.data
  const unsavedSections = draft !== null && saved !== undefined ? changedSections(draft, saved) : []
  const hasUnsaved = draft !== null && saved !== undefined && (unsavedSections.length > 0 || stableStringify(draft) !== stableStringify(saved))
  const staffingUnsaved = unsavedSections.includes('staffing')
  // A difference no section names (a field only the store adds) still marks the settings tabs, as it always has.
  const settingsUnsaved = hasUnsaved && (unsavedSections.length === 0 || unsavedSections.some((name) => name !== 'staffing'))
  useEffect(() => {
    onUnsavedChange({ any: hasUnsaved, staffing: staffingUnsaved, settings: settingsUnsaved })
    return () => onUnsavedChange(NOTHING_UNSAVED)
  }, [hasUnsaved, staffingUnsaved, settingsUnsaved, onUnsavedChange])

  if (configQuery.isPending) {
    return <Skeleton lines={5} label="Loading configuration" />
  }

  if (isNotFoundError(configQuery.error)) {
    // Under the facility's record the Details tab already says enough; the not-configured screen belongs to the settings tabs.
    return section === 'staffing' ? null : <FacilityNotConfigured facilityId={facilityId} />
  }

  if (configQuery.isError || draft === null) {
    return <LoadFailure message="Failed to load facility configuration." onRetry={() => void configQuery.refetch()} retrying={configQuery.isFetching} />
  }

  const isDirty = hasUnsaved
  function updateThresholds(patch: Partial<FacilityConfig['tierThresholds']>): void {
    setDraft((prev) => (prev ? { ...prev, tierThresholds: { ...prev.tierThresholds, ...patch } } : prev))
  }
  function updateTiming(patch: Partial<FacilityConfig['bidWindowTiming']>): void {
    setDraft((prev) => (prev ? { ...prev, bidWindowTiming: { ...prev.bidWindowTiming, ...patch } } : prev))
  }
  function updateBridge(patch: Partial<FacilityConfig['clinicalBridge']>): void {
    setDraft((prev) => (prev ? { ...prev, clinicalBridge: { ...prev.clinicalBridge, ...patch } } : prev))
  }
  const scoring = draft.scoring ?? DEFAULT_SCORING_RULES
  function updateScoring(patch: Partial<ScoringRules>): void {
    setDraft((prev) => (prev ? { ...prev, scoring: { ...(prev.scoring ?? DEFAULT_SCORING_RULES), ...patch } } : prev))
  }

  return (
    <div className="flex flex-col gap-4">
      <div className="text-[11px] text-ink-mid">Set for this facility only. Saving applies every settings tab's changes at once.</div>

      <div className="flex flex-col gap-4">
        {section === 'thresholds' && (
        <ConfigCard title="Tier Thresholds" help="facilities.config-tiers">
          <NumberRow
            label="Gold — minimum score"
            value={draft.tierThresholds.goldMinScore}
            onChange={(v) => updateThresholds({ goldMinScore: v })}
          />
          <NumberRow
            label="Silver — minimum score"
            value={draft.tierThresholds.silverMinScore}
            onChange={(v) => updateThresholds({ silverMinScore: v })}
          />
          <NumberRow
            label="Bronze — minimum score"
            value={draft.tierThresholds.bronzeMinScore}
            onChange={(v) => updateThresholds({ bronzeMinScore: v })}
          />
          <ReadOnlyRow label="Probationary — below" value={String(draft.tierThresholds.bronzeMinScore)} />
          <TextRow
            label="Tiebreaker within tier"
            value={draft.tierThresholds.tiebreaker}
            onChange={(v) => updateThresholds({ tiebreaker: v })}
          />
        </ConfigCard>
        )}

        {section === 'bidWindows' && (
        <ConfigCard title="Bid Window Timing" help="facilities.bid-windows">
          <p className="mb-2 text-[11px] text-ink-mid leading-relaxed">
            Wording for workers about when each tier's bidding opens. The Gold, Silver and Bronze lines are what a worker one tier below is told they would unlock, on their standing and in their messages; a line left unset says nothing. These settings do not open or close bidding: workers bid in the one preferences window set on the Scheduling tab.
          </p>
          <TextRow
            label="Scoring period closes"
            hint="When a month's scores become final, in words. Display only: a scoring period is a calendar month in the facility's time zone, and this text does not move the close."
            value={draft.bidWindowTiming.scoringPeriodCloses}
            onChange={(v) => updateTiming({ scoringPeriodCloses: v })}
          />
          <TextRow
            label="Gold window opens"
            hint="What a Silver worker is told Gold would let them bid from, on their standing and in their messages. Wording only: it does not open bidding."
            value={draft.bidWindowTiming.goldWindowOpensAt}
            onChange={(v) => updateTiming({ goldWindowOpensAt: v })}
          />
          <NullableNumberRow
            label="Gold window duration"
            hint="How many hours Gold would have the shifts to itself before Silver. Not used: every tier that bids saves preferences in the same window."
            value={draft.bidWindowTiming.goldWindowDurationHours}
            onChange={(v) => updateTiming({ goldWindowDurationHours: v })}
            suffix="hrs"
          />
          <NullableTextRow
            label="Silver window opens"
            hint="What a Bronze worker is told Silver would let them bid from. Not set: they are told nothing about it."
            value={draft.bidWindowTiming.silverWindowOpensAt}
            onChange={(v) => updateTiming({ silverWindowOpensAt: v })}
          />
          <NullableTextRow
            label="Bronze window opens"
            hint="What a Probationary worker is told Bronze would let them bid from. Not set: they are told nothing about it."
            value={draft.bidWindowTiming.bronzeWindowOpensAt}
            onChange={(v) => updateTiming({ bronzeWindowOpensAt: v })}
          />
          <ReadOnlyRow label="Probationary" hint="How the lowest tier gets its month: it does not bid, its shifts are assigned. Read-only here; which tiers bid is set under Who bids on the Scheduling tab." value={draft.bidWindowTiming.probationaryAssignment} />
        </ConfigCard>
        )}

        {section === 'staffing' && (
          <SchedulingGroup title="Units, credentials and minimum staffing" help="facilities.scheduling-staffing" intro="Who may work where, what each role must hold, and how many a shift needs. A facility that keeps none of these schedules as before.">
            <FacilityStaffingRules rules={schedulingRulesOf(draft)} shiftUnitIds={shiftUnitIds} onChange={(patch) => setDraft((prev) => (prev ? { ...prev, scheduling: { ...schedulingRulesOf(prev), ...patch } } : prev))} />
          </SchedulingGroup>
        )}

        {section === 'automation' && <FacilityAutomationCard facilityId={facilityId} rules={schedulingRulesOf(draft)} onChange={(scheduling) => setDraft((prev) => (prev ? { ...prev, scheduling } : prev))} />}
        {section === 'scheduling' && <FacilitySchedulingCard rules={schedulingRulesOf(draft)} onOpenUnits={onOpenDetails} onChange={(scheduling) => setDraft((prev) => (prev ? { ...prev, scheduling } : prev))} />}

        {section === 'bridge' && (
        <ConfigCard title="Clinical Bridge & Premiums" help="facilities.config-bridge">
          <p className="mb-2 text-[11px] text-ink-mid leading-relaxed">
            On-demand coverage. Workers you mark eligible (on their Edit worker form or the roster import's <code className="font-mono">bridgeEligible</code> column) enroll for days they would cover a call-out, and see these rules in their app. Switching Clinical Bridge off stops new enrollments; days already enrolled stay on record.
          </p>
          <ToggleRow
            label="Clinical Bridge enabled"
            checked={draft.clinicalBridge.enabled}
            onChange={(v) => updateBridge({ enabled: v })}
          />
          <ToggleRow
            label="OT permitted at this facility"
            checked={draft.clinicalBridge.otPermitted}
            onChange={(v) => updateBridge({ otPermitted: v })}
          />
          <NumberRow
            label="Response window (0 = until the shift starts)"
            value={draft.clinicalBridge.responseWindowMinutes}
            onChange={(v) => updateBridge({ responseWindowMinutes: v })}
            suffix="min"
          />
          <NumberRow
            label="Arrival window after accepting"
            value={draft.clinicalBridge.arrivalWindowMinutes ?? DEFAULT_ARRIVAL_WINDOW_MINUTES}
            onChange={(v) => updateBridge({ arrivalWindowMinutes: v })}
            suffix="min"
          />
          <NumberRow
            label="Score boost per Bridge shift worked"
            value={draft.clinicalBridge.scoreBoostPerShift}
            onChange={(v) => updateBridge({ scoreBoostPerShift: v })}
            suffix="pts/shift"
          />
          <ToggleRow
            label="Pay premium"
            checked={draft.clinicalBridge.payPremiumEnabled}
            onChange={(v) => updateBridge({ payPremiumEnabled: v })}
          />
          <NullableNumberRow
            label="Pay premium per hour"
            value={draft.clinicalBridge.payPremiumAmount}
            onChange={(v) => updateBridge({ payPremiumAmount: v })}
            suffix="$"
          />
          <ReadOnlyRow label="Unfilled escalation path" value={draft.clinicalBridge.unfilledEscalationPath.join(' → ')} />
          <TextRow
            label="Score recalc frequency"
            value={draft.clinicalBridge.scoreRecalcFrequency}
            onChange={(v) => updateBridge({ scoreRecalcFrequency: v })}
          />
        </ConfigCard>
        )}

        {section === 'notifications' && <FacilityDeliveryCard facilityId={facilityId} rules={deliveryRulesOf(draft)} onChange={(delivery) => setDraft((prev) => (prev ? { ...prev, delivery } : prev))} />}

        {section === 'prioritization' && (
        <ConfigCard title="Partner Prioritization" help="facilities.config-prioritization">
          <ToggleRow
            label="Rank partner pools by reliability"
            checked={draft.prioritizationEnabled ?? true}
            onChange={(v) => setDraft((prev) => (prev ? { ...prev, prioritizationEnabled: v } : prev))}
          />
          <p className="text-[11px] text-ink-mid leading-relaxed pt-2">
            When on, a scheduling partner that asks this facility to rank its qualified workers gets them back ordered by tier, then score. When
            off, the partner gets its own order back unchanged. Either way no worker is added or removed.
          </p>
        </ConfigCard>
        )}

        {section === 'scoring' && (
        <ConfigCard title="Scoring Rules" help="facilities.config-scoring">
          <NumberRow label="Shifts before a score is final" value={scoring.minShifts} onChange={(v) => updateScoring({ minShifts: Math.max(0, Math.round(v)) })} suffix="shifts" />
          <NumberRow label="Grace for arrival and departure" value={scoring.graceMinutes} onChange={(v) => updateScoring({ graceMinutes: Math.max(0, Math.round(v)) })} suffix="min" />
          <NumberRow label="Short notice is under" value={scoring.shortNoticeHours} onChange={(v) => updateScoring({ shortNoticeHours: Math.max(0, v) })} suffix="hrs" />
          <NumberRow label="A short-notice call-out counts as" value={scoring.shortNoticeMultiplier} onChange={(v) => updateScoring({ shortNoticeMultiplier: Math.max(1, v) })} suffix="×" />
          <NumberRow label="New hires stay probationary for" value={scoring.newHireProbationDays} onChange={(v) => updateScoring({ newHireProbationDays: Math.max(0, Math.round(v)) })} suffix="days" />
          <ToggleRow label="A provisional score can lower a tier" checked={scoring.provisionalCanMoveDown} onChange={(v) => updateScoring({ provisionalCanMoveDown: v })} />
          <NumberRow label="A denied PTO day, worked, adds" value={scoring.ptoDeniedWorkedPoints ?? 2} onChange={(v) => updateScoring({ ptoDeniedWorkedPoints: Math.min(20, Math.max(0, v)) })} suffix="pts" />
          <NumberRow label="A shift filled inside 24 hours, worked, adds" value={scoring.lateFillPoints ?? 2} onChange={(v) => updateScoring({ lateFillPoints: Math.min(20, Math.max(0, v)) })} suffix="pts" />
          <NumberRow label="A late Clinical Bridge withdrawal takes off" value={scoring.lateBridgeWithdrawalPoints ?? 1} onChange={(v) => updateScoring({ lateBridgeWithdrawalPoints: Math.min(20, Math.max(0, v)) })} suffix="pts" />
          <p className="text-[11px] text-ink-mid leading-relaxed pt-2">
            How the scoring engine treats this facility’s outcomes. With fewer than the minimum shifts a score is provisional and blended with the
            previous final score. What each behavior counts for, and the levels that earn 100 and 0, are set on the Behaviors tab. The three
            scheduling events add or take off points on top of the score, like the Clinical Bridge boost: a credit only once the shift is worked,
            a filled gap only when the worker took it first come (never a supervisor's override), and 0 switches one off.
          </p>
        </ConfigCard>
        )}
      </div>

      <div className="flex items-center gap-3">
        {/* After a save the draft becomes what the API stored, so a field it does not keep (an older deployment's schema) shows as reverted rather than as still unsaved. */}
        <Button icon={Save} disabled={!isDirty || updateMutation.isPending} onClick={() => updateMutation.mutate(withoutBlankStaffingRows(draft), { onSuccess: (stored) => setDraft(stored) })}>
          {updateMutation.isPending ? 'Saving…' : 'Save Configuration'}
        </Button>
        {isDirty ? (
          <span className="text-xs text-ink-mid">{unsavedSections.length > 0 ? `Unsaved changes in ${listLabels(unsavedSections)}` : 'Unsaved changes'}</span>
        ) : (
          updateMutation.isSuccess && <span className="text-xs text-green font-bold">Saved</span>
        )}
        {updateMutation.isError && <span className="text-xs text-coral font-bold">Failed to save. Try again.</span>}
      </div>
    </div>
  )
}

/** A unit row left with no id is dropped at the save rather than refused by the API; the rest of the draft is sent as it stands. */
function withoutBlankStaffingRows(config: FacilityConfig): FacilityConfig {
  if (!config.scheduling) {
    return config
  }
  const rules = schedulingRulesOf(config)
  // Beds and the planned census ride along: before 21 October 2026 a save here dropped them, and the Month card then read every day as having nothing to forecast from.
  return { ...config, scheduling: { ...rules, units: rules.units.filter((unit) => unit.id.trim() !== '').map((unit) => ({ id: unit.id.trim(), name: unit.name.trim() || unit.id.trim(), beds: unit.beds ?? null, plannedCensus: unit.plannedCensus ?? null })) } }
}
